"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useRouter } from "@frontend/lib/navigation";
import { Modal } from "@frontend/components/modal";
import { Button, cx } from "@frontend/components/ui";
import {
  enableCallsAction,
  requestCallAction,
  startCallAction,
} from "@frontend/lib/actions";
import type { CollabCallState } from "@creators/backend/lib/collabs/calls";
import { formatPhoneDisplay } from "@creators/backend/lib/phone";

type ModalKind = "request" | "enable" | "start" | null;

/**
 * Phone control in the collab chat header. Request / enable / place a call
 * each go through a confirm modal; place-call shows the number that will ring.
 */
export function CallHeaderButton({
  collabId,
  viewer,
  calls: initial,
  canAct,
  viewerPhone,
}: {
  collabId: string;
  viewer: "creator" | "brand";
  calls: CollabCallState;
  canAct: boolean;
  /** The signed-in party's number — shown so they know which phone will ring. */
  viewerPhone: string;
}) {
  const router = useRouter();
  const [calls, setCalls] = useState(initial);
  const [modal, setModal] = useState<ModalKind>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setCalls(initial);
  }, [initial]);

  // Room sync already refreshes the page; only backstop while a call is live.
  useEffect(() => {
    if (!initial.activeCall) return;
    const id = window.setInterval(() => {
      router.refresh();
    }, 15_000);
    return () => window.clearInterval(id);
  }, [initial.activeCall, router]);

  if (calls.secondsRemaining <= 0 && !calls.activeCall) {
    return null;
  }

  if (!canAct && !calls.enabled && !calls.requested) {
    return null;
  }

  const phoneDisplay = formatPhoneDisplay(viewerPhone);
  const enabled = calls.enabled;
  const requested = calls.requested;
  const busy = Boolean(calls.activeCall);

  const openForClick = () => {
    setError(null);
    if (busy) return;
    if (enabled && calls.canPlaceCall) {
      setModal("start");
      return;
    }
    if (viewer === "brand" && !enabled) {
      if (requested) return;
      setModal("request");
      return;
    }
    if (viewer === "creator" && !enabled) {
      setModal("enable");
    }
  };

  const confirm = async () => {
    if (!modal) return;
    setPending(true);
    setError(null);
    const form = new FormData();
    form.set("collabId", collabId);
    const action =
      modal === "request"
        ? requestCallAction
        : modal === "enable"
          ? enableCallsAction
          : startCallAction;
    const result = await action({}, form);
    setPending(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setModal(null);
    router.refresh();
  };

  const ariaLabel = busy
    ? "Call in progress"
    : enabled
      ? "Start a call"
      : viewer === "brand"
        ? requested
          ? "Call requested"
          : "Request a call"
        : "Enable calls";

  const iconTone = busy
    ? "bg-money-600/20 text-money-600 ring-money-600/40"
    : enabled
      ? "bg-money-600 text-canvas hover:opacity-90"
      : requested && viewer === "brand"
        ? "bg-canvas text-ink-subtle ring-line"
        : "bg-canvas text-ink-muted ring-line hover:bg-surface hover:text-ink";

  return (
    <>
      <button
        type="button"
        disabled={busy || (requested && viewer === "brand" && !enabled) || pending}
        onClick={openForClick}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={cx(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-full ring-1 transition disabled:cursor-default disabled:opacity-70",
          iconTone,
          busy ? "animate-pulse" : null,
        )}
      >
        <PhoneIcon />
      </button>

      {modal === "request" ? (
        <Modal
          title="Request a call?"
          description="The creator can turn on proxy calls for this collab."
          onClose={() => !pending && setModal(null)}
          size="sm"
        >
          <ConfirmBody
            error={error}
            pending={pending}
            confirmLabel="Request call"
            pendingLabel="Requesting…"
            onCancel={() => setModal(null)}
            onConfirm={() => void confirm()}
          >
            <p className="text-sm leading-relaxed text-ink-muted">
              Nothing rings yet — this only asks the creator to enable calls in this chat.
            </p>
          </ConfirmBody>
        </Modal>
      ) : null}

      {modal === "enable" ? (
        <Modal
          title="Enable calls?"
          description="Either side can start a call after you enable."
          onClose={() => !pending && setModal(null)}
          size="sm"
        >
          <ConfirmBody
            error={error}
            pending={pending}
            confirmLabel="Enable calls"
            pendingLabel="Enabling…"
            onCancel={() => setModal(null)}
            onConfirm={() => void confirm()}
          >
            <p className="text-sm leading-relaxed text-ink-muted">
              The collaborator is dialled first, then you. Your number for the bridge:
            </p>
            <p className="mt-2 rounded-xl border border-line bg-canvas px-3.5 py-2.5 text-sm font-medium tabular text-ink">
              {phoneDisplay}
            </p>
          </ConfirmBody>
        </Modal>
      ) : null}

      {modal === "start" ? (
        <Modal
          title="Start call?"
          description="Confirm to place the proxy call now."
          onClose={() => !pending && setModal(null)}
          size="sm"
        >
          <ConfirmBody
            error={error}
            pending={pending}
            confirmLabel="Call now"
            pendingLabel="Calling…"
            confirmVariant="money"
            onCancel={() => setModal(null)}
            onConfirm={() => void confirm()}
          >
            <p className="text-sm leading-relaxed text-ink-muted">
              {viewer === "brand"
                ? "We’ll call you first on this number, then connect the creator:"
                : "We’ll call the collaborator first, then you on this number:"}
            </p>
            <p className="mt-2 rounded-xl border border-line bg-canvas px-3.5 py-2.5 text-sm font-medium tabular text-ink">
              {phoneDisplay}
            </p>
          </ConfirmBody>
        </Modal>
      ) : null}
    </>
  );
}

function ConfirmBody({
  children,
  error,
  pending,
  confirmLabel,
  pendingLabel,
  confirmVariant = "primary",
  onCancel,
  onConfirm,
}: {
  children: ReactNode;
  error: string | null;
  pending: boolean;
  confirmLabel: string;
  pendingLabel: string;
  confirmVariant?: "primary" | "money";
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="space-y-4">
      {children}
      {error ? <p className="text-sm text-alert-600">{error}</p> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant={confirmVariant}
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? pendingLabel : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3.5"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
